import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { useCurrency } from "@/lib/currency";
import { CURRENCY_META, formatAmount } from "@/lib/api";
import { Minus, Plus, Divide } from "lucide-react";
import AutobetPanel from "./AutobetPanel";

export default function BetControls({ amount, setAmount, onPlay, busy, playLabel = "Place Bet", extra, disabled, autoPlaceBet }) {
  const [mode, setMode] = useState("manual");
  const { user } = useAuth();
  const { currency } = useCurrency();
  const bal = user?.balances?.[currency] ?? 0;
  const meta = CURRENCY_META[currency];

  const adjust = (fn) => () => setAmount(Math.max(0, Number(fn(Number(amount) || 0).toFixed(8))));

  return (
    <div className="panel p-5 flex flex-col gap-4" data-testid="bet-controls">
      {autoPlaceBet && (
        <div className="grid grid-cols-2 gap-1 p-1 rounded-xl" style={{ background: "#0F0F11" }} data-testid="bet-mode-tabs">
          {["manual", "auto"].map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`py-2 rounded-lg text-xs uppercase tracking-widest ${mode === m ? "bg-white/[0.08] text-amber-400" : "text-zinc-500 hover:text-white"}`}
              style={{ transition: "background-color 180ms ease, color 180ms ease" }}
              data-testid={`bet-mode-${m}`}
            >
              {m}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-zinc-500">Bet Amount</span>
        <button
          className="text-[10px] text-zinc-500 hover:text-amber-400 uppercase tracking-wider font-mono-num"
          onClick={() => setAmount(bal)}
          data-testid="bet-max"
        >
          Max · {formatAmount(bal, currency)} {currency}
        </button>
      </div>

      <div className="flex gap-2">
        <div className="flex-1 panel flex items-center gap-2 px-3 py-2" style={{ background: "#0F0F11" }}>
          <span className="w-2 h-2 rounded-full" style={{ background: meta.color }} />
          <input
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            data-testid="bet-amount-input"
            className="flex-1 bg-transparent outline-none text-right font-mono-num text-lg"
          />
          <span className="text-xs text-zinc-500 font-mono-num">{currency}</span>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2">
        <button className="btn-ghost rounded-lg py-2 text-xs" onClick={adjust((v) => v / 2)} data-testid="bet-half">
          <Divide size={12} className="inline mr-1" /> 2
        </button>
        <button className="btn-ghost rounded-lg py-2 text-xs" onClick={adjust((v) => v * 2)} data-testid="bet-double">
          ×2
        </button>
        <button className="btn-ghost rounded-lg py-2 text-xs flex items-center justify-center" onClick={adjust((v) => v - 1)} data-testid="bet-minus">
          <Minus size={12} />
        </button>
        <button className="btn-ghost rounded-lg py-2 text-xs flex items-center justify-center" onClick={adjust((v) => v + 1)} data-testid="bet-plus">
          <Plus size={12} />
        </button>
      </div>

      {extra}

      {mode === "auto" && autoPlaceBet ? (
        <AutobetPanel amount={amount} setAmount={setAmount} placeBet={autoPlaceBet} currency={currency} disabled={!user} />
      ) : (
      <button
        onClick={onPlay}
        disabled={busy || disabled}
        className="btn-gold rounded-xl py-4 font-display font-bold text-base tracking-wide mt-1"
        data-testid="bet-play"
      >
        {busy ? "Rolling…" : playLabel}
      </button>
      )}
    </div>
  );
}
